import { useState } from 'react';
import Modal from '../shared/Modal';
import { sendAvailabilityRequest } from '../../utils/api';

// Bulk-send the "When can you serve?" availability link to a team's members.
// Mirrors SendScheduleLinksModal but targets the /serve/:personId page.
export default function RequestAvailabilityModal({ open, onClose, showToast, teamName, people = [], senderEmail, canSend }) {
  const [sending, setSending] = useState(false);

  const eligible = people.filter(p => p.email && p.id);

  async function handleSendAll() {
    if (!canSend) {
      showToast('Only @thebelonging.co staff can send availability requests.');
      return;
    }
    if (eligible.length === 0) {
      showToast('No team members with email addresses found.');
      return;
    }
    setSending(true);
    let successCount = 0;
    let errorCount = 0;
    for (const person of eligible) {
      try {
        await sendAvailabilityRequest(person.email, person.name, person.id, senderEmail);
        successCount++;
      } catch (err) {
        console.error(`Failed to send to ${person.name}:`, err);
        errorCount++;
      }
    }
    setSending(false);
    if (errorCount === 0) {
      showToast(`Availability requests sent to ${successCount} people.`);
    } else {
      showToast(`Sent ${successCount}, failed ${errorCount}. Check Apps Script logs.`);
    }
    onClose();
  }

  function handleClose() {
    if (sending) return;
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Request Availability"
      footer={
        <>
          <button className="btn" onClick={handleClose} disabled={sending}>Cancel</button>
          <button
            className={`btn btn-dark${(!canSend || eligible.length === 0 || sending) ? ' btn-disabled' : ''}`}
            onClick={handleSendAll}
            disabled={!canSend || eligible.length === 0 || sending}
          >
            {sending ? 'Sending…' : `Send to ${eligible.length} ${eligible.length === 1 ? 'Person' : 'People'}`}
          </button>
        </>
      }
    >
      {!canSend && (
        <div className="notice-bar" style={{ background: '#fff8ed', border: '1px solid #f5c542', color: '#8a6200' }}>
          Only @thebelonging.co staff can send availability requests.
        </div>
      )}
      {canSend && (
        <div className="notice-bar">
          This sends each member of {teamName || 'this team'} a personal link to mark which days and times they can serve. Their answers show up here as you build the schedule.
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
          Will be sent to ({eligible.length} {eligible.length === 1 ? 'person' : 'people'})
        </div>
        {eligible.length === 0 ? (
          <div style={{ padding: '16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            No team members with email addresses found.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
            {eligible.slice(0, 50).map(p => (
              <div key={p.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                {p.name}
                <span style={{ fontFamily: 'var(--font-b)', fontWeight: 300, color: 'var(--muted)', marginLeft: 8 }}>{p.email}</span>
              </div>
            ))}
            {eligible.length > 50 && (
              <div style={{ padding: '10px 16px', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
                + {eligible.length - 50} more
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
